import { useState, useEffect, useCallback } from 'react';
import analytics from '../lib/analytics';

export interface ConsentState {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentStatus {
  hasConsent: boolean;
  timestamp: number;
  version: string;
}

const CONSENT_STORAGE_KEY = 'penticton-dev-consent';
const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 365;

const DEFAULT_CONSENT: ConsentState = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const useConsent = () => {
  const [consentState, setConsentState] = useState<ConsentState>(DEFAULT_CONSENT);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({
    hasConsent: false,
    timestamp: 0,
    version: CONSENT_VERSION,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load consent from localStorage on mount
  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
        console.log(
          '🔍 Loading consent from localStorage:',
          stored ? 'Found data' : 'No data found',
        );

        if (stored) {
          const parsed = JSON.parse(stored);
          const { consent, status } = parsed;

          // Check if consent is still valid
          const expiryTime = status.timestamp + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
          const isExpired = Date.now() > expiryTime;
          const isOldVersion = status.version !== CONSENT_VERSION;

          console.log('📅 Consent validation:', {
            hasConsent: status.hasConsent,
            isExpired,
            isOldVersion,
            currentVersion: CONSENT_VERSION,
            storedVersion: status.version,
          });

          if (isExpired || isOldVersion) {
            // Clear expired or outdated consent
            console.log('🗑️ Clearing expired/outdated consent');
            localStorage.removeItem(CONSENT_STORAGE_KEY);
            setConsentState(DEFAULT_CONSENT);
            setConsentStatus({
              hasConsent: false,
              timestamp: 0,
              version: CONSENT_VERSION,
            });
          } else {
            console.log('✅ Loading valid consent:', consent);
            setConsentState(consent);
            setConsentStatus(status);
            // Update analytics with the loaded consent
            analytics.setConsent(consent.analytics);
          }
        } else {
          console.log('ℹ️ No previous consent found, using defaults');
          setConsentState(DEFAULT_CONSENT);
          setConsentStatus({
            hasConsent: false,
            timestamp: 0,
            version: CONSENT_VERSION,
          });
        }
      } catch (error) {
        console.error('❌ Failed to load consent state:', error);
        // Clear potentially corrupted data
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        setConsentState(DEFAULT_CONSENT);
        setConsentStatus({
          hasConsent: false,
          timestamp: 0,
          version: CONSENT_VERSION,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadConsent();
  }, []);

  // Save consent to localStorage and update analytics
  const saveConsent = useCallback((consent: ConsentState) => {
    try {
      const status: ConsentStatus = {
        hasConsent: true,
        timestamp: Date.now(),
        version: CONSENT_VERSION,
      };

      const toStore = {
        consent,
        status,
      };

      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(toStore));
      setConsentState(consent);
      setConsentStatus(status);

      // Update analytics consent
      analytics.setConsent(consent.analytics);

      // Track consent event (only if analytics consent is granted)
      if (consent.analytics) {
        analytics.trackEvent('consent_given', {
          analytics: consent.analytics,
          functional: consent.functional,
          marketing: consent.marketing,
        });
      }

      console.log('✅ Consent updated successfully:', consent, 'Status:', status);
    } catch (error) {
      console.error('Failed to save consent:', error);
    }
  }, []);

  const updateConsent = useCallback(
    (newConsent: ConsentState) => {
      saveConsent(newConsent);
    },
    [saveConsent],
  );

  const resetConsent = useCallback(() => {
    try {
      console.log('🔄 Resetting consent to defaults');
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setConsentState(DEFAULT_CONSENT);
      setConsentStatus({
        hasConsent: false,
        timestamp: 0,
        version: CONSENT_VERSION,
      });

      // Reset analytics consent
      analytics.setConsent(false);
      console.log('✅ Consent reset successfully');
    } catch (error) {
      console.error('❌ Failed to reset consent:', error);
    }
  }, []);

  const isConsentRequired = useCallback(() => {
    return analytics.isConsentRequired();
  }, []);

  const shouldShowBanner = useCallback(() => {
    return analytics.isConsentRequired() && !consentStatus.hasConsent && !isLoading;
  }, [consentStatus.hasConsent, isLoading]);

  const hasAnalyticsConsent = useCallback(() => {
    return consentState.analytics && consentStatus.hasConsent;
  }, [consentState.analytics, consentStatus.hasConsent]);

  const hasFunctionalConsent = useCallback(() => {
    return consentState.functional && consentStatus.hasConsent;
  }, [consentState.functional, consentStatus.hasConsent]);

  const hasMarketingConsent = useCallback(() => {
    return consentState.marketing && consentStatus.hasConsent;
  }, [consentState.marketing, consentStatus.hasConsent]);

  return {
    // State
    consentState,
    consentStatus,
    isLoading,

    // Actions
    updateConsent,
    resetConsent,

    // Helpers
    isConsentRequired,
    shouldShowBanner,
    hasAnalyticsConsent,
    hasFunctionalConsent,
    hasMarketingConsent,
  };
};
